import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Receipt, ArrowsClockwise, ArrowSquareOut } from "@phosphor-icons/react";

const TOGGLES = [
  ["show_logo", "Company logo"], ["show_company_address", "Company address"], ["show_department", "Department"],
  ["show_designation", "Designation & date of joining"], ["show_pan_uan", "PAN & UAN"], ["show_bank", "Bank & account no."],
  ["show_lop", "Loss-of-pay days"], ["show_employer_contribution", "Employer PF / ESIC"], ["show_ctc", "Monthly CTC"],
  ["show_tds_source", "TDS basis (regime)"], ["amount_in_words", "Net pay in words"], ["hide_zero_rows", "Hide zero-value rows"],
];

export default function PayslipSettingsPanel({ params }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/payslip-settings", { params }).then(({ data }) => setS(data.settings)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params.tenant_id]);

  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/payslip-settings", { settings: s, ...params }); setS(data.settings); toast.success("Payslip format saved"); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!s) return <div className="text-sm text-zinc-500">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="payslip-settings-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display text-xl font-bold flex items-center gap-2"><Receipt size={20} /> Payslip format</div>
          <div className="text-xs text-zinc-500 mt-1">Choose which fields appear on the payslip PDF and rename salary components. Logo and colours come from the Branding tab. The sample on the right updates as you edit.</div>
        </div>
        <Button onClick={save} disabled={busy} className="btn-zenith rounded-sm" data-testid="payslip-settings-save">{busy ? "Saving…" : "Save format"}</Button>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <div className="xl:col-span-2 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-1 gap-4">
          <div className="border border-zinc-200 bg-white rounded-sm p-5 space-y-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Fields shown</div>
            {TOGGLES.map(([k, l]) => (
              <div key={k} className="flex items-center justify-between"><Label className="text-xs">{l}</Label><Switch checked={!!s[k]} onCheckedChange={(v) => setS({ ...s, [k]: v })} data-testid={`payslip-toggle-${k}`} /></div>
            ))}
          </div>
          <div className="space-y-4">
            <div className="border border-zinc-200 bg-white rounded-sm p-5 space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Text</div>
              <div><Label className="text-xs">Document title</Label><Input value={s.title} onChange={(e) => setS({ ...s, title: e.target.value })} className="rounded-sm mt-1" data-testid="payslip-title" /></div>
              <div><Label className="text-xs">Authorised signatory (optional)</Label><Input value={s.signatory || ""} onChange={(e) => setS({ ...s, signatory: e.target.value })} placeholder="e.g. For Acme Technologies Pvt Ltd — HR Head" className="rounded-sm mt-1" data-testid="payslip-signatory" /></div>
              <div><Label className="text-xs">Footer note</Label><Textarea value={s.footer_note || ""} onChange={(e) => setS({ ...s, footer_note: e.target.value })} className="rounded-sm mt-1" data-testid="payslip-footer" /></div>
            </div>
            <div className="border border-zinc-200 bg-white rounded-sm p-5 space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Component labels</div>
              {Object.entries(s.labels).map(([k, v]) => (
                <div key={k}><Label className="text-[10px] text-zinc-500 font-mono-plex">{k}</Label><Input value={v} onChange={(e) => setS({ ...s, labels: { ...s.labels, [k]: e.target.value } })} className="rounded-sm mt-0.5 h-8 text-xs" data-testid={`payslip-label-${k}`} /></div>
              ))}
            </div>
          </div>
        </div>
        <LivePreview settings={s} params={params} />
      </div>
    </div>
  );
}

function LivePreview({ settings, params }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const timer = useRef(null);
  const render = async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await api.post("/admin/payslip-settings/preview", { settings, ...params }, { responseType: "blob" });
      setUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(data); });
    } catch (e) { setErr(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(render, 600);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line
  }, [JSON.stringify(settings), params.tenant_id]);
  return (
    <div className="xl:col-span-3 border border-zinc-200 bg-zinc-50 rounded-sm flex flex-col min-h-[720px]" data-testid="payslip-live-preview">
      <div className="px-4 py-2 border-b border-zinc-200 bg-white flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Live preview · sample data{loading && <span className="ml-2 normal-case tracking-normal text-emerald-700">rendering…</span>}</div>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={render} className="rounded-sm h-7" data-testid="payslip-preview-refresh"><ArrowsClockwise size={12} className="mr-1" /> Refresh</Button>
          <Button size="sm" variant="ghost" onClick={() => url && window.open(url, "_blank")} disabled={!url} className="rounded-sm h-7" data-testid="payslip-preview-open"><ArrowSquareOut size={12} className="mr-1" /> Open</Button>
        </div>
      </div>
      {err ? <div className="p-4 text-xs text-red-700" data-testid="payslip-preview-error">{err}</div>
        : url ? <iframe title="Payslip preview" src={`${url}#toolbar=0&navpanes=0&view=FitH`} className="flex-1 w-full" data-testid="payslip-preview-frame" />
        : <div className="flex-1 grid place-items-center text-xs text-zinc-500">Rendering sample payslip…</div>}
    </div>
  );
}
